import { useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Trash2, AlertTriangle, RotateCcw } from "lucide-react";
import { Section } from "./Section";

export function DangerZone({ cleanup, setCleanup, runCleanup, testing }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const doReset = async () => {
    setBusy(true);
    try {
      await api.post("/admin/factory-reset", { password: pwd, confirm });
      toast.success(t("set.factoryDone"));
      localStorage.removeItem("lang");
      setTimeout(() => { window.location.href = "/setup"; }, 800);
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  return (
    <>
      <Section icon={Trash2} title={t("set.dbTitle")}>
        <p className="mb-4 text-xs text-zinc-400">{t("set.dbIntro")}</p>
        <div className="flex flex-wrap gap-4">
          {[["users", t("set.users")], ["groups", t("nav.groups")], ["teams", t("teams.title")], ["licenses", t("lic.title")], ["workflows", t("set.workflows")]].map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-zinc-700">
              <Switch checked={cleanup[k]} onCheckedChange={(v) => setCleanup({ ...cleanup, [k]: v })} data-testid={`cleanup-${k}`} /> {lbl}
            </label>
          ))}
        </div>
        <Button variant="outline" onClick={runCleanup} disabled={testing === "cleanup"} data-testid="settings-cleanup-run" className="mt-4 rounded-sm border-red-300 text-red-600 hover:bg-red-50">
          <Trash2 size={15} className="mr-2" /> {t("set.clearSelected")}
        </Button>
      </Section>

      <section className="border border-red-300 bg-red-50/40">
        <div className="flex items-center gap-2 border-b border-red-200 bg-red-50 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-red-700">
          <AlertTriangle size={15} /> {t("set.factoryTitle")}
        </div>
        <div className="p-5">
          <p className="mb-4 text-sm text-zinc-600">{t("set.factoryIntro")}</p>
          <Button variant="outline" onClick={() => { setPwd(""); setConfirm(""); setOpen(true); }} data-testid="factory-reset-btn" className="rounded-sm border-red-400 bg-white text-red-600 hover:bg-red-600 hover:text-white">
            <RotateCcw size={15} className="mr-2" /> {t("set.factoryBtn")}
          </Button>
        </div>
      </section>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="factory-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base text-red-700"><AlertTriangle size={16} /> {t("set.factoryDialogTitle")}</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">{t("set.factoryWarn")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.factoryPwd")}</Label>
              <Input type="password" autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="factory-reset-password" className="rounded-sm border-zinc-300" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.factoryConfirmLabel")}</Label>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t("set.factoryConfirmPlaceholder")} data-testid="factory-reset-confirm" className="rounded-sm border-zinc-300 font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} className="rounded-sm border-zinc-300">{t("common.cancel")}</Button>
            <Button onClick={doReset} disabled={busy || !pwd || confirm.trim() !== "RESET"} data-testid="factory-reset-execute" className="rounded-sm bg-red-600 hover:bg-red-700">
              <Trash2 size={15} className="mr-2" /> {busy ? t("common.saving") : t("set.factoryConfirmBtn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
